import { faCheckCircle, faPen } from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import { ListItemButton } from '@mui/material';
// eslint-disable-next-line no-restricted-imports
import Badge from '@mui/material/Badge';
// eslint-disable-next-line no-restricted-imports
import Card from '@mui/material/Card';
// eslint-disable-next-line no-restricted-imports
import Divider from '@mui/material/Divider';
// eslint-disable-next-line no-restricted-imports
import IconButton from '@mui/material/IconButton';
// eslint-disable-next-line no-restricted-imports
import List from '@mui/material/List';
// eslint-disable-next-line no-restricted-imports
import ListItem from '@mui/material/ListItem';
// eslint-disable-next-line no-restricted-imports
import ListItemAvatar from '@mui/material/ListItemAvatar';
// eslint-disable-next-line no-restricted-imports
import ListItemText from '@mui/material/ListItemText';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { CircularProgress } from 'src/components/CircularProgress';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { Tab, Tabs } from 'src/components/Tabs';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { fetchResources as fetchAppointmentResources } from 'src/models/appointment/appointment';
import { Checkin } from 'src/models/checkin/_types';
import {
  getDependentDisplayName,
  getDependentInitials,
} from 'src/models/user/utils';
import { getUserAddress } from 'src/models/user/utils';
import { getVisitStatus, guestVisits } from 'src/models/visit/utils';
import { fetchResources as fetchVisitResources } from 'src/models/visit/visit';
import { ServiceCount } from 'src/shared/ServiceCount';
import { UserNotes } from 'src/shared/UserNotes';
import { formatMonthDayYear, formatTime } from 'src/utils/dateFormatter';

import type { Appointment } from 'src/models/appointment/_types';
import type { AccountManager, Alert, User } from 'src/models/user/_types';
import type { Visit } from 'src/models/visit/_types';
import type { UserPackageWithPaymentPlan } from 'src/models/visit/visit';
import type { Identifier } from 'types/utilityTypes';

interface Props {
  visit: Visit;
  visitPackage?: UserPackageWithPaymentPlan;
  user: User;
  appointment?: Appointment;
  clientTags?: string[];
  checkinUser?: (barcode: string) => void;
  setCurrentVisitId?: (id: string) => void;
  currentCheckin?: Checkin;
  checkinError?: string | null;
  alerts?: Alert[];
  hideDependentTab?: boolean;
}

export const VisitTabs: React.FC<Props> = (props) => {
  const { t } = useTranslation('translation', { keyPrefix: 'visit' });
  const [activeTab, setActiveTab] = useState('visit');
  // prevent loading dependent on user with no dependents
  const showDependents =
    !props.hideDependentTab && !!props.user?.dependents?.length;
  if (!showDependents && activeTab === 'dependents') setActiveTab('visit');

  return (
    <>
      <Box
        sx={{
          px: 2,
          pt: 4,
        }}
      >
        <Tabs
          sx={{ px: 2 }}
          value={activeTab}
          onChange={(_e, value) => setActiveTab(value)}
        >
          <Tab value="visit" label={t('visit')} />
          <Tab value="contact" label={t('contact')} />
          <Tab value="training" label={t('privateNotes')} />
          {showDependents && <Tab value="dependents" label={t('dependents')} />}
        </Tabs>
      </Box>
      <Box
        sx={{
          px: 2,
          pb: 4,
          pt: 2,
        }}
      >
        {activeTab === 'visit' && (
          <Card>
            <VisitTab {...props} />
          </Card>
        )}
        {activeTab === 'contact' && (
          <Card>
            <ContactTab {...props} />
          </Card>
        )}
        {activeTab === 'training' && (
          <UserNotes userId={props.user?.id} embedded />
        )}
        {activeTab === 'dependents' && (
          <Card>
            <DependentsTab {...props} />
          </Card>
        )}
      </Box>
    </>
  );
};

const AvatarWithBadge = ({ dep, majorAlerts }) => {
  return (
    <Badge
      overlap="circular"
      color={majorAlerts ? 'error' : 'secondary'}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={'•'}
    >
      <Avatar src={dep?.dependentProfileImage}>
        {getDependentDisplayName(dep)}
      </Avatar>
    </Badge>
  );
};

export const DependentRow = (dep, checkedIn, alerts, openGymVisitText) => {
  return (
    <>
      <ListItemAvatar>
        {alerts?.length ? (
          <AvatarWithBadge
            dep={dep}
            majorAlerts={alerts.some((a) => a.color === 'red')}
          />
        ) : (
          <Avatar src={dep.dependentProfileImage}>
            {getDependentInitials(dep)}
          </Avatar>
        )}
      </ListItemAvatar>
      <ListItemText
        primary={getDependentDisplayName(dep)}
        secondary={
          checkedIn
            ? checkedIn.visit.appointment?.serviceName || openGymVisitText
            : null
        }
      />
    </>
  );
};

interface checkinArray {
  userId: Identifier;
  alerts: Alert[];
  checkin: Checkin;
}

export const DependentsTab: React.FC<Props> = ({
  visit,
  user,
  checkinUser,
  setCurrentVisitId,
  currentCheckin,
  checkinError,
  alerts,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'visit' });
  const barcode = (code) => {
    return code || '';
  };
  const [checkedIn, setCheckedIn] = useState([] as checkinArray[]);
  const [loading, setLoading] = useState([] as Identifier[]);

  const checkIn = (barcode: string, dep: AccountManager) => {
    if (checkinUser) {
      setLoading([...loading, dep.dependentId]);
      checkinUser(barcode);
    }
  };

  const isCheckedIn = (dep) => {
    return !!getCheckedIn(dep.dependentId);
  };
  const notCheckedIn = (dep) => {
    return !isCheckedIn(dep);
  };
  const getCheckedIn = (id) => {
    return checkedIn.find((c) => id === c.userId);
  };

  // load latest checkin data
  // handles current checkin, errors, and alerts
  if (
    currentCheckin &&
    !checkinError &&
    !getCheckedIn(currentCheckin?.user?.id)
  ) {
    setCheckedIn([
      ...checkedIn,
      {
        checkin: currentCheckin,
        alerts: alerts || [],
        userId: currentCheckin.user.id,
      },
    ]);
    setLoading([]);
  } else if (checkinError && loading.length > 0) {
    setLoading([]);
  }
  if (getCheckedIn(currentCheckin?.user?.id) && (alerts || []).length > 0) {
    const cur = getCheckedIn(currentCheckin?.user?.id);
    if (cur) cur.alerts = alerts || [];
  }

  const isLoading = loading.length > 0;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          startIcon={<Icon icon={faPen} />}
          component={Link}
          size="small"
          href={`clients/${visit.clientId}/dependents/`}
          color="primary"
          sx={{ m: 1 }}
        >
          {t('manageDependents')}
        </Button>
      </Box>
      <List>
        {(user.dependents || []).map((dep) => (
          <ListItem
            key={dep.id}
            secondaryAction={
              notCheckedIn(dep) && (
                <IconButton
                  edge="end"
                  aria-label="check-in"
                  disabled={isLoading}
                  onClick={() =>
                    checkIn(barcode(dep.dependentBarcodeHash), dep)
                  }
                >
                  {loading.indexOf(dep.dependentId) > -1 && (
                    <CircularProgress size="small" />
                  )}
                  {loading.indexOf(dep.dependentId) === -1 && (
                    <Icon
                      icon={faCheckCircle}
                    />
                  )}
                </IconButton>
              )
            }
            disablePadding
          >
            {isCheckedIn(dep) && setCurrentVisitId && (
              <ListItemButton
                onClick={() =>
                  setCurrentVisitId(
                    String(
                      getCheckedIn(dep.dependentId)?.checkin?.visit?.id || ''
                    )
                  )
                }
              >
                {DependentRow(
                  dep,
                  getCheckedIn(dep.dependentId)?.checkin,
                  getCheckedIn(dep.dependentId)?.alerts,
                  t('openGymVisit')
                )}
              </ListItemButton>
            )}
            {notCheckedIn(dep) && (
              <ListItemButton>
                {DependentRow(dep, null, null, t('openGymVisit'))}
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
    </>
  );
};

export const ContactTab: React.FC<Props> = ({ visit, user, clientTags }) => {
  const { session } = useSession();
  const { t } = useTranslation('translation', { keyPrefix: 'visit' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          startIcon={<Icon icon={faPen} />}
          component={Link}
          href={`clients/${visit.clientId}/`}
          color="primary"
          size="small"
          sx={{ m: 1 }}
        >
          {tCommon('buttons.editProfile')}
        </Button>
      </Box>
      <InfoListItem title={t('memberSince')}>
        {formatMonthDayYear(user.createdAt)}
      </InfoListItem>
      {!!user.birthdate && (
        <InfoListItem title={tCommon('labels.birthday')}>
          {formatMonthDayYear(user.birthdate)}
        </InfoListItem>
      )}
      <InfoListItem title={tCommon('labels.tags')}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {clientTags?.map((tag) => {
            return (
              <Chip
                key={tag}
                sx={{ textTransform: 'capitalize' }}
                label={tag}
                size="small"
              />
            );
          })}
        </Stack>
      </InfoListItem>
      <InfoListItem title={tCommon('labels.email')}>{user.email}</InfoListItem>
      <InfoListItem title={tCommon('labels.phone')}>
        {user.phoneNumber}
      </InfoListItem>
      <InfoListItem title={tCommon('labels.address')}>
        {getUserAddress(user)}
      </InfoListItem>
      <InfoListItem title={t('barcodeId')}>{user.barcodeHash}</InfoListItem>
      <InfoListItem title={t('platformId')}>
        {session?.id || t('noPlatformId')}
      </InfoListItem>
    </>
  );
};

export const VisitTab: React.FC<Props> = ({
  visit,
  visitPackage,
  appointment,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'visit' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const guests = guestVisits(visit, appointment?.visits || []);
  const guestsCount = t('guests', { count: guests.length });
  const guestList = guests?.map((v) => v.userName).join(', ');
  const { data: appointmentResources, isLoading: appointmentResourcesLoading } =
    useQuery({
      queryKey: ['appointment_resources', appointment?.id],

      queryFn: () => {
        if (!appointment) return [];

        return fetchAppointmentResources(appointment);
      },
    });

  const { data: visitResources, isLoading: visitResourcesLoading } = useQuery({
    queryKey: ['visit_resources', visit?.id],

    queryFn: () => {
      if (!visit) return [];

      return fetchVisitResources(visit);
    },
  });

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          startIcon={<Icon icon={faPen} />}
          component={Link}
          href={`/ex4/clients/${visit.clientId}/visits/`}
          size="small"
          color="primary"
          sx={{ m: 1 }}
        >
          {t('manageVisits')}
        </Button>
      </Box>
      <InfoListItem title={t('service')}>
        {appointment?.serviceName || t('openGymVisit')}
      </InfoListItem>
      <InfoListItem title={t('session')}>
        {appointment?.startTime && (
          <>
            {formatMonthDayYear(visit.appointment?.startTime)}
            <br />
            {formatTime(appointment.startTime)} -{' '}
            {formatTime(appointment.endTime)}
          </>
        )}
        {!appointment?.startTime && !!visit.checkInAt && (
          <>
            {formatMonthDayYear(visit.checkInAt)}
            <br />
            {formatTime(visit.checkInAt)}
            {visit.checkOutAt && ` - ${formatTime(visit.checkOutAt)}`}
          </>
        )}
      </InfoListItem>
      <InfoListItem title={t('staff')}>
        {appointment?.trainerNames ? (
          <>{appointment?.trainerNames.join(', ')}</>
        ) : (
          <>{visitPackage?.assignedTrainerName}</>
        )}
      </InfoListItem>
      <InfoListItem title={t('assets')}>
        {!visitResourcesLoading &&
          visitResources.length > 0 &&
          visitResources.map((resource) => resource.name).join(', ')}
        <br />
        {!appointmentResourcesLoading &&
          appointmentResources.length > 0 &&
          appointmentResources.map((resource) => resource.name).join(', ')}
      </InfoListItem>
      <InfoListItem title={t('bookedBy')}>
        {visit.bookedByName || t('openGymVisit')}
        <br />
        {formatMonthDayYear(visit.createdAt)}
      </InfoListItem>
      <InfoListItem title={tCommon('labels.status')}>
        {getVisitStatus(visit)}
        {visit.status == 'completed' && visitPackage?.id && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {t('packageUsed')}
            <Icon
              color="success"
              icon={faCheckCircle}
            />
          </Box>
        )}
      </InfoListItem>
      <InfoListItem title={t('package')}>
        {visitPackage?.id && (
          <>
            {visitPackage.name}
            <br />
            {t('packageId')}: {visitPackage.id}
            <br />
            {visitPackage.expiresOn
              ? t('expires') + ': ' + formatMonthDayYear(visitPackage.expiresOn)
              : t('neverExpires')}
          </>
        )}
        {!visitPackage?.id && (
          <Typography variant="body2" color="error">
            {t('noPackageFound')}
          </Typography>
        )}
      </InfoListItem>
      <InfoListItem title={t('serviceLimit')}>
        {visitPackage?.serviceCounts?.[0] && (
          <ServiceCount
            count={visitPackage.serviceCounts[0]}
            isTrainerView
            userPackageId={visitPackage.id}
            serviceCountIndex={0}
          />
        )}
      </InfoListItem>
      {guests.length > 0 && (
        <InfoListItem title={guestsCount}>{guestList}</InfoListItem>
      )}
    </>
  );
};

interface InfoListItemProps {
  title: string;
  children: React.ReactNode;
}

const InfoListItem: React.FC<InfoListItemProps> = ({ title, children }) => (
  <>
    <Divider />
    <Box
      sx={{
        display: 'flex',
        py: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '50%',
          pl: 3,
        }}
      >
        <Typography variant="subtitle2" component="div">
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          width: '50%',
          pr: 3,
          overflowWrap: 'break-word',
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            width: '100%',
            color: 'text.secondary',
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  </>
);
